import { ColDef } from 'ag-grid-community'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import { formatDateWithAMPM } from 'utils/date'

import { CSUserGridDropdown } from '../components/CSUserGridDropdown'
import { activationStatusDisplayName, IOaiUserStub, PermissionList } from '../types/userTypes'
import { CSGridPermissions } from './CSGridUserPermissions'
import { GridColumnLongNameToShort } from './GridColumLongNameToShort'
import { GridColumnSimple } from './GridColumnSimple'

export const USER_COLUMN_DEFS: ColDef<IOaiUserStub>[] = [
  {
    field: 'givenName',
    headerName: 'First Name',
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellRenderer: (params: any) => GridColumnSimple(params.data.givenName, 20),
    cellClassRules: {
      'cell-copied-highlight': (params: any) => {
        return (
          params.context?.copiedCell &&
          params.data?.userGuid === params.context.copiedCell.rowId &&
          params.colDef.field === params.context.copiedCell.field
        )
      },
    },
    onCellDoubleClicked: (params: any) => {
      if (params.data?.givenName && params.context?.setCopiedCell) {
        navigator.clipboard.writeText(params.data.givenName)
        params.context.setCopiedCell({ rowId: params.data.userGuid, field: 'givenName' })
      }
    },
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      suppressAndOrCondition: true,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  {
    field: 'familyName',
    headerName: 'Last Name',
    filter: 'agTextColumnFilter',
    wrapText: true,
    cellRenderer: (params: any) => GridColumnSimple(params.data.familyName, 20),
    cellClassRules: {
      'cell-copied-highlight': (params: any) => {
        return (
          params.context?.copiedCell &&
          params.data?.userGuid === params.context.copiedCell.rowId &&
          params.colDef.field === params.context.copiedCell.field
        )
      },
    },
    onCellDoubleClicked: (params: any) => {
      if (params.data?.familyName && params.context?.setCopiedCell) {
        navigator.clipboard.writeText(params.data.familyName)
        params.context.setCopiedCell({ rowId: params.data.userGuid, field: 'familyName' })
      }
    },
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      suppressAndOrCondition: true,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    filter: 'agTextColumnFilter',
    wrapText: true,
    width: 120,
    minWidth: 220,
    cellRenderer: (params: any) => GridColumnLongNameToShort(params.data.email, 20, params),
    cellClassRules: {
      'cell-copied-highlight': (params: any) => {
        return (
          params.context?.copiedCell &&
          params.data?.userGuid === params.context.copiedCell.rowId &&
          params.colDef.field === params.context.copiedCell.field
        )
      },
    },
    onCellDoubleClicked: (params: any) => {
      if (params.data?.email && params.context?.setCopiedCell) {
        navigator.clipboard.writeText(params.data.email)
        params.context.setCopiedCell({ rowId: params.data.userGuid, field: 'email' })
      }
    },
    filterParams: {
      filterOptions: ['contains', 'equals', 'startsWith'],
      suppressAndOrCondition: true,
      buttons: ['apply', 'cancel'],
      closeOnApply: true,
    },
  },
  {
    field: 'emailVerified',
    headerName: 'Email Verified',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: (params: any) => {
      return params.data?.emailVerified ? 'Verified' : 'Not Verified'
    },
  },
  {
    field: 'activationStatus',
    headerName: 'User Status',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: (params: any) => {
      return params.data?.activationStatus === UserActivationStatus.Ok ? 'Enabled' : 'Disabled'
    },
  },
  {
    field: 'practiceLastLoggedInName',
    headerName: 'Practice Last Logged In',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: (params: any) => {
      return params.data?.practiceLastLoggedInName ? GridColumnSimple(params.data.practiceLastLoggedInName, 20) : 'N/A'
    },
  },
  {
    field: 'lastLogin',
    headerName: 'Last Login',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    valueFormatter: (params: any) => {
      if (!params.value) return 'N/A'
      return formatDateWithAMPM(params.value)
    },
  },
  {
    field: 'permissions',
    headerName: 'Permissions',
    filter: false,
    wrapText: true,
    sortable: false,
    suppressHeaderMenuButton: true,
    valueFormatter: (params: any) => {
      if (!params.value) return ''
      if (Array.isArray(params.value)) {
        return params.value
          .map((item: any) => item?.name || item?.permissionName || item?.id || String(item))
          .filter(Boolean)
          .join(', ')
      }
      if (typeof params.value === 'object') {
        return params.value?.name || params.value?.permissionName || ''
      }
      return String(params.value)
    },
    cellRenderer: CSGridPermissions,
  },
  {
    field: undefined,
    headerName: '',
    pinned: 'right',
    cellClass: 'actions-button-cell',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    width: 110,
    cellRenderer: CSUserGridDropdown,
  },
]

export const formatActivationStatus = (params: any) => {
  const status = activationStatusDisplayName(params.data.activationStatus)
  return status
}

export enum UserActivationStatus {
  Invited,
  Ok,
  Unknown,
  Disabled,
  InvitationExpired,
}

export const getPermissionList = () => {
  return [
    {
      id: IUserRoleGuidEnum.claimsAccess,
      name: 'Claim Access',
    },
    {
      id: IUserRoleGuidEnum.fileShare,
      name: 'File Share',
    },
    {
      id: IUserRoleGuidEnum.batchUpload,
      name: 'Batch Upload',
    },
    {
      id: IUserRoleGuidEnum.runAutoCheck,
      name: 'Run AutoCheck',
    },
  ] as PermissionList[]
}

export const extraPermissions = () => {
  return [
    {
      id: IUserRoleGuidEnum.dataMapping,
      name: 'Data Mapping',
    },
    {
      id: IUserRoleGuidEnum.manageCustomer,
      name: 'Manage Customer',
    },
    {
      id: IUserRoleGuidEnum.manageTenant,
      name: 'Manage Tenant',
    },
    {
      id: IUserRoleGuidEnum.manageUser,
      name: 'Manage User',
    },
    {
      id: IUserRoleGuidEnum.managePayerConfig,
      name: 'Manage Payer Config',
    },
    {
      id: IUserRoleGuidEnum.manageGenericRules,
      name: 'Manage Generic Rules',
    },
    {
      id: IUserRoleGuidEnum.backendAdmin,
      name: 'Backend Admin',
    },
    {
      id: IUserRoleGuidEnum.payerConfigAdmin,
      name: 'Payer Config Admin',
    },
    {
      id: IUserRoleGuidEnum.apiAdmin,
      name: 'Api Admin',
    },
    {
      id: IUserRoleGuidEnum.superAdmin,
      name: 'Super Admin',
    },
  ] as PermissionList[]
}
